import { FC } from 'react';

import { Heading, Link } from '@purpur/library';
import { Icon } from '@purpur/library/icon';

import { useTranslations } from '@telialt/common-cms/src/useTranslations';
import { getSvgIcon } from '@telialt/common-icon/src/utils';
import { VasProduct } from '@telialt/models/src/EshopModel';
import { selectRentingPlanVASProducts } from '@telialt/utils/src/eshopSelectors';

import { useCartQuery } from '../../hooks/queries/useCartQuery';
import { RatePlanVasLegalText } from '../../product/LegalText';
import { ESHOP_TEST_ID } from '../../test/test-ids';
import { VasClickHandler } from './model';
import { PlanVas } from './PlanVas';
import { useRatePlanVas } from './useVasData';
import {
    RentVasDescription,
    RentVasList,
    RentVasListItem,
    RentVasSection,
    RentVasTitle,
} from './ValueAddedServices.styles';
import { VasBlock } from './VasBlock';

export const RatePlanVasBlock: FC<VasClickHandler> = ({ onClick }) => {
    const { t } = useTranslations();
    const ratePlanVasList = useRatePlanVas();

    if (!ratePlanVasList?.length) {
        return null;
    }

    return (
        <PlanVas
            sectionTitle={t['eshop.product_page.vas.rate_plan_block.title']}
            vasList={ratePlanVasList}
            onClick={onClick}
            vasLegalText={<RatePlanVasLegalText />}
            data-test={ESHOP_TEST_ID.RATE_PLAN_VAS_BLOCK}
        />
    );
};

export const RentVasBlock: FC = () => {
    const { t } = useTranslations();
    const { cart } = useCartQuery();
    const rentVasList = selectRentingPlanVASProducts(cart);

    if (!rentVasList?.length) {
        return null;
    }

    return (
        <RentVasSection data-test={ESHOP_TEST_ID.RENT_PLAN_VAS_BLOCK}>
            <Heading tag="h3" variant="title-100">
                {t['eshop.product_page.vas.rent_plan_block.title']}
            </Heading>
            <RentVasList>
                {rentVasList.map(({ title, description, iconType }, index) => (
                    <RentVasListItem key={[title, iconType].join('') || index}>
                        <Icon svg={getSvgIcon(iconType)} size="lg" />
                        <RentVasTitle>{title}</RentVasTitle>
                        <RentVasDescription content={description} />
                    </RentVasListItem>
                ))}
            </RentVasList>
            <Link
                href={t['eshop.product_page.vas.rent_plan_block.link.href']}
                variant="text"
                target="_blank"
                disableVisited={true}
            >
                {t['eshop.product_page.vas.rent_plan_block.link.text']}
            </Link>
        </RentVasSection>
    );
};

type OrderVasBlockProps = { segment?: string };
export const OrderVasBlock: FC<OrderVasBlockProps> = ({ segment }) => {
    const { t } = useTranslations();

    const vasListB2B: VasProduct[] = [
        {
            iconType: 'delivery',
            title: t['eshop.product_page.vas.free_delivery_b2b.title'],
            description: t['eshop.product_page.vas.free_delivery_b2b.description'],
        },
    ];
    const vasListB2C: VasProduct[] = [
        {
            iconType: 'delivery',
            title: t['eshop.product_page.vas.free_delivery.title'],
            description: t['eshop.product_page.vas.free_delivery.description'],
        },
        {
            iconType: 'reverse',
            title: t['eshop.product_page.vas.money_back_guarantee.title'],
            description: t['eshop.product_page.vas.money_back_guarantee.description'],
        },
        {
            iconType: 'credit-card',
            title: t['eshop.product_page.vas.payment.title'],
            description: t['eshop.product_page.vas.payment.description'],
        },
    ];

    const vasList = segment === 'b2b' ? vasListB2B : vasListB2C;
    const dataTest = segment === 'b2b' ? ESHOP_TEST_ID.ORDER_VAS_BLOCK_B2B : ESHOP_TEST_ID.ORDER_VAS_BLOCK;

    return <VasBlock vasList={vasList} dataTest={dataTest} />;
};
